import classNames from 'classnames';
import { useEffect, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import ReactOnRails from 'react-on-rails';
import { Provider, useSelector } from 'react-redux';
import { renderImages } from '@common/CMS/helpers/content';
import { usePrepareLookBook } from '@common/CMS/helpers/lookBook';
import { parseProducts } from '@common/CMS/helpers/templates';
import * as cmsComponents from '@common/cmsComponents';
import { IN_LISTING_BANNERS_IDENTIFIER } from '@common/Listing/constants/InlistingBanners';
import { initPromotionTracking } from '@common/Tracking/GaTracking/GaPromotions';
import { isSmallScreen } from '@helpers/responsiveHelper';
import { getBannerSSRHtmlNodes } from '../helpers/inlistingBanners';

import './InlistingBanners.scss';

const getBannerPosition = (mobile, desktop) => (isSmallScreen() ? Number(mobile) + 1 : Number(desktop) + 1);

function injectCSS(CSSContent) {
    return new Promise((resolve, reject) => {
        let styleTag = null;

        if (CSSContent) {
            styleTag = document.createElement('style');
            const head = document.head || document.getElementsByTagName('head')[0];
            styleTag.appendChild(document.createTextNode(CSSContent));
            head.appendChild(styleTag);

            if (!document.head.contains(styleTag)) {
                reject(new Error('Failed to inject Inlisting banners CSS'));
            }
        }

        resolve(styleTag);
    });
}

function InlistingBanners({ containerElement }) {
    const inListingBanners = useSelector(state => state.listing.inListingBanners);
    const [shouldLoadBanners, setShouldLoadBanners] = useState(false);
    const { banners = [] } = inListingBanners ?? {};
    const { phrases } = inListingBanners ?? {};
    // TODO: Remove this hook when issue with missing header is fixed
    // https://boozt.atlassian.net/browse/PLT-27749
    usePrepareLookBook(phrases);
    const isMobile = isSmallScreen();

    const renderSSRComponents = useCallback(() => {
        const nodesList = getBannerSSRHtmlNodes(banners);

        nodesList.forEach((node) => {
            const { reactId, ...props } = node.dataset;
            const Component = cmsComponents[reactId];

            if (Component) {
                const root = createRoot(node);

                root.render(
                    <Provider store={ReactOnRails.getStore('store')}>
                        <Component {...props} />
                    </Provider>
                );
            }
        });
    }, [banners]);

    useEffect(() => {
        let scriptTag = null;

        if (shouldLoadBanners) {
            const { js } = inListingBanners ?? {};

            if (js) {
                scriptTag = document.createElement('script');
                scriptTag.setAttribute('data-id', 'cms-editor-js');
                scriptTag.type = 'text/javascript';
                scriptTag.innerHTML = js;
                document.body.appendChild(scriptTag);

                if (typeof window !== 'undefined' && typeof window.__bootWebpackJsonp !== 'undefined') {
                    window.__bootWebpackJsonp();
                }
            }

            parseProducts(inListingBanners);
            renderImages();
            renderSSRComponents();
            initPromotionTracking(containerElement?.current);
        } else {
            const { css } = inListingBanners ?? {};

            injectCSS(css).then(() => {
                setShouldLoadBanners(!!inListingBanners);
            });
        }

        return () => {
            if (scriptTag) {
                document.body.removeChild(scriptTag);
            }
        };
    }, [inListingBanners, shouldLoadBanners, renderSSRComponents, containerElement]);

    if (!shouldLoadBanners) {
        return null;
    }

    return (
        banners.map((banner, index) => {
            const {
                html,
                options: {
                    mobileListingPosition,
                    desktopListingPosition
                }
            } = banner;
            return (
                <div
                    key={desktopListingPosition}
                    className={classNames(`${IN_LISTING_BANNERS_IDENTIFIER} cms-block-item`, {
                        'cms-container--mobile': isMobile,
                        'cms-container--desktop': !isMobile
                    })}
                    style={{
                        gridRow: getBannerPosition(mobileListingPosition, desktopListingPosition)
                    }}
                >
                    <div
                        className={classNames(`cms-block-container--${index}`)}
                        /* eslint-disable-next-line react/no-danger */
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                </div>
            );
        })
    );
}

export default InlistingBanners;
